import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TransferComplianceCard } from "@/components/suppliers/TransferComplianceCard";
import {
  Truck, PackageSearch, ArrowRight, FileSpreadsheet, FileText as FileTextIcon,
  Building2, Calendar, AlertTriangle, Boxes, ArrowRightLeft, Handshake,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");

/**
 * Hub de proveedores: un único punto donde el laboratorio que entra puede
 * elegir entre "Pedido reposición" o "Pedido lo que nos falta" en pocos
 * clicks, ver un resumen rápido de proveedores y descargar el Excel/PDF.
 */
export default function ProveedoresHubPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryMode, setSummaryMode] = useState(null); // 'reposicion' | 'falta'

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Sólo cargamos el endpoint LIGERO del modo reposición para tener
        // el resumen de proveedores. El modo "missing" se navega bajo demanda.
        const r = await api.get("/orders/replenishment", { params: { days: 30 } });
        setOverview(r.data);
      } catch (e) {
        // sin dispensación o sin stock: mantenemos overview=null y mostramos hint
        setOverview({ error: true });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadFaltaSummary = async () => {
    setSummaryMode("falta");
    setShowSummary(true);
    try {
      const r = await api.get("/orders/missing", { params: { days: 30 } });
      setOverview(r.data);
    } catch (e) {
      // ignore: overview de reposición ya está
    }
  };

  const showReposicionSummary = () => {
    setSummaryMode("reposicion");
    setShowSummary(true);
  };

  const dispLabel = overview?.dispensation?.label;
  const stockDate = overview?.stock?.uploaded_at;
  const suppliers = overview?.suppliers || [];
  const totalEur = suppliers.reduce((s, x) => s + (x.total_eur || 0), 0);
  const totalUnits = suppliers.reduce((s, x) => s + (x.total_units || 0), 0);

  const cardCls =
    "group bg-white border border-slate-200 rounded-lg p-6 hover:border-slate-900 hover:shadow-lg transition-all";

  return (
    <div className="p-8 lg:p-12 max-w-[1600px]" data-testid="proveedores-hub">
      <TransferComplianceCard />

      {/* Hero · vídeo */}
      <div className="relative mb-10 rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200" data-testid="proveedores-hero-video">
        <video
          src="/video/farmaclass-proveedores.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-auto max-h-[380px] object-cover block"
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-950/25 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-8 lg:px-12 max-w-2xl pointer-events-none">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-300 mb-2">FarmaClass · Proveedores</div>
          <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-white leading-tight">
            Del pedido a la cubeta, en dos clicks.
          </h2>
          <p className="text-sm text-white/70 mt-2 max-w-lg">
            Genera Excels listos para tu ERP y reasigna laboratorios al vuelo. Tu farmacia decide, tu proveedor entrega.
          </p>
        </div>
      </div>

      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Proveedores</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Centro de pedidos</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Elige el tipo de pedido. En 2 clicks tu laboratorio se lleva el Excel listo para el ERP.
        </p>
      </div>

      {/* Resumen del estudio */}
      {!loading && overview && !overview.error && (
        <div
          className="mb-8 bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
          data-testid="hub-summary"
        >
          <div className="inline-flex items-center gap-2 text-slate-700">
            <Calendar className="h-4 w-4 text-slate-400" />
            Estudio base: <strong className="text-slate-900">{dispLabel || "—"}</strong>
          </div>
          {stockDate && (
            <div className="inline-flex items-center gap-2 text-slate-700">
              <Boxes className="h-4 w-4 text-slate-400" />
              Stock a {fmtDate(stockDate)}
            </div>
          )}
          {!stockDate && (
            <div className="inline-flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              Sin stock cargado: los pedidos no podrán calcular faltas reales.{" "}
              <Link to="/stock" className="underline font-semibold">
                Subir stock →
              </Link>
            </div>
          )}
        </div>
      )}

      {!loading && (overview?.error || !dispLabel) && (
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800" data-testid="hub-no-data">
          <strong>No hay estudio cargado.</strong> Sube una dispensación antes de generar pedidos.{" "}
          <Link to="/upload" className="underline font-semibold">Subir dispensación →</Link>
        </div>
      )}

      {/* Dos accesos rápidos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className={cardCls} data-testid="card-falta">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-rose-50 grid place-items-center text-rose-700 shrink-0 group-hover:bg-rose-100 transition-colors">
              <PackageSearch className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-rose-700 mb-1">Conversión marca → genérico</div>
              <div className="font-display text-2xl font-bold tracking-tight text-slate-950 mb-2">Lo que nos falta</div>
              <p className="text-sm text-slate-500">
                Sólo lo que aún compras como marca y deberías pasar a EFG. Cada línea va al proveedor con condición que hayas marcado.
              </p>
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <Link
                  to="/pedido/falta"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 hover:underline"
                  data-testid="link-falta"
                >
                  Generar pedido <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  type="button"
                  onClick={loadFaltaSummary}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:underline"
                  data-testid="preview-falta"
                >
                  Ver proveedores aquí ↓
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={cardCls} data-testid="card-reposicion">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-blue-50 grid place-items-center text-blue-700 shrink-0 group-hover:bg-blue-100 transition-colors">
              <Truck className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-700 mb-1">Reposición completa</div>
              <div className="font-display text-2xl font-bold tracking-tight text-slate-950 mb-2">Pedido reposición</div>
              <p className="text-sm text-slate-500">
                Genéricos ya dispensados + conversión marca→genérico (en rojo). Pedido completo listo para enviar.
              </p>
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <Link
                  to="/pedido/reposicion"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 hover:underline"
                  data-testid="link-reposicion"
                >
                  Generar pedido <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  type="button"
                  onClick={showReposicionSummary}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
                  data-testid="preview-reposicion"
                >
                  Ver proveedores aquí ↓
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tercer acceso: Traslado simple à la carte */}
      <div className={`${cardCls} mb-12`} data-testid="card-traslado-simple">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-emerald-50 grid place-items-center text-emerald-700 shrink-0 group-hover:bg-emerald-100 transition-colors">
            <ArrowRightLeft className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-1">A la carta · libre elección</div>
            <div className="font-display text-2xl font-bold tracking-tight text-slate-950 mb-2">Traslado simple</div>
            <p className="text-sm text-slate-500">
              Selecciona TÚ las moléculas que quieras trasladar — 1 a 1 o de 5 en 5 — y elige libremente a qué laboratorio (con o sin condición). Ideal para pedidos puntuales fuera de tus 3 proveedores habituales.
            </p>
            <div className="mt-4">
              <Link
                to="/pedido/traslado-simple"
                className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 hover:underline"
                data-testid="link-traslado-simple"
              >
                Abrir selector de moléculas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Top proveedores resumen — sólo visible bajo demanda */}
      {showSummary && suppliers.length > 0 && (
        <div data-testid="hub-supplier-summary">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-display text-xl font-semibold tracking-tight">
              Tus proveedores ·{" "}
              <span className={summaryMode === "falta" ? "text-rose-700" : "text-blue-700"}>
                {summaryMode === "falta" ? "Lo que nos falta" : "Pedido reposición"}
              </span>
            </h2>
            <div className="text-xs text-slate-500 font-mono-num">
              {suppliers.length} laboratorios · {fmtNum(totalUnits)} uds · {totalEur.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
                <tr>
                  <th className="p-3 text-left font-semibold">Laboratorio</th>
                  <th className="p-3 text-right font-semibold">Líneas</th>
                  <th className="p-3 text-right font-semibold">Unidades</th>
                  <th className="p-3 text-right font-semibold">Valor €</th>
                  <th className="p-3 text-right font-semibold">% del total</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {suppliers.slice(0, 12).map((s) => {
                  const pct = totalEur ? (s.total_eur / totalEur) * 100 : 0;
                  return (
                    <tr key={s.laboratorio} className="hover:bg-slate-50" data-testid={`hub-supplier-${s.laboratorio}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{s.laboratorio}</span>
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono-num">{fmtNum(s.lines?.length || 0)}</td>
                      <td className="p-3 text-right font-mono-num">{fmtNum(s.total_units)}</td>
                      <td className="p-3 text-right font-mono-num font-semibold">{s.total_eur?.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 })}</td>
                      <td className="p-3 text-right font-mono-num text-slate-500">{pct.toFixed(1)}%</td>
                      <td className="p-3 text-right">
                        <Link
                          to={`/pedido/reposicion?lab=${encodeURIComponent(s.laboratorio)}`}
                          className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors inline-flex items-center gap-1"
                          data-testid={`hub-open-${s.laboratorio}`}
                        >
                          Ver pedido <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Atajos: condiciones + stock */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
        <Link to="/proveedores/condiciones" className="bg-emerald-50/60 border border-emerald-300 rounded-lg p-4 hover:border-emerald-600 hover:shadow-sm transition-all" data-testid="hub-link-conditions">
          <div className="flex items-center gap-2 font-semibold text-emerald-900">
            <Handshake className="h-4 w-4 text-emerald-600" /> Condiciones de laboratorios
          </div>
          <div className="text-xs text-emerald-800/70 mt-1">Convenios, descuentos y vademécum por laboratorio (con importación IA).</div>
        </Link>
        <Link to="/stock" className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-900 hover:shadow-sm transition-all" data-testid="hub-link-stock">
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <Boxes className="h-4 w-4 text-slate-500" /> Stock farmacia
          </div>
          <div className="text-xs text-slate-500 mt-1">Subir el último export del programa de gestión.</div>
        </Link>
        {isAdmin && (
          <Link to="/catalog" className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-900 hover:shadow-sm transition-all" data-testid="hub-link-catalog">
            <div className="flex items-center gap-2 font-semibold text-slate-900">
              <FileSpreadsheet className="h-4 w-4 text-slate-500" /> Catálogo CN
            </div>
            <div className="text-xs text-slate-500 mt-1">Revisa qué laboratorios tienes con cada G.H.</div>
          </Link>
        )}
        <Link to="/history" className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-900 hover:shadow-sm transition-all" data-testid="hub-link-history">
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <FileTextIcon className="h-4 w-4 text-slate-500" /> Cambiar estudio base
          </div>
          <div className="text-xs text-slate-500 mt-1">Selecciona otro periodo como referencia.</div>
        </Link>
      </div>

      {loading && (
        <div className="text-center text-slate-400 py-12" data-testid="hub-loading">
          Calculando proveedores…
        </div>
      )}
    </div>
  );
}
